import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { isQueryReady } from "src/api/axiosClient/utils.ts";
import { hydraSearchToAppSearch } from "src/api/_common/hydra/search.ts";
import { zodParseWithLog } from "src/api/apiErrorLogs.ts";

export const getAllPermissionsKey = "allPermissions" as const;

type Options = {
    cacheTime?: number;
    search?: string;
    params?: AxiosParams;
};

export const useGetAllPermissions = (options?: Options) => {
    const { cacheTime = 0, search = "", params = {} } = options || {};

    const query = useQuery({
        queryKey: [getAllPermissionsKey, search, JSON.stringify(params)],
        staleTime: 0,
        cacheTime: cacheTime,
        queryFn: async () => {
            const url = "/api/permissions";

            const request = await loggedInApiCall.get(url, {
                params: {
                    q: search !== "" ? search : undefined,
                    ...params,
                },
            });

            const totalItems = request.data["hydra:totalItems"];
            const permission = request.data["hydra:member"];
            const appSearch = hydraSearchToAppSearch(request.data["hydra:search"]);
            const data = zodParseWithLog(permission, PermissionSchema.array(), "allPermissions");
            return {
                permissions: data,
                totalItems: totalItems,
                appSearch,
            };
        },
        onError: () => {
            return {
                permissions: [],
                totalItems: 0,
                appSearch: {},
            };
        },
    });

    const isReady = isQueryReady(query, true);
    return {
        ...query.data,
        isReady,
        ...query,
    };
};

export type PermissionScope = "org" | "platform";

export type Permission = {
    "@id": IRI;
    id: number;
    identifier: string;
    slug: string;
    object: string;
    extra?: string | null;
    scope?: PermissionScope;
};

export const PermissionSchema: z.ZodSchema<Permission> = z.object({
    "@id": z.string(),
    id: z.number(),
    identifier: z.string(),
    slug: z.string(),
    object: z.string(),
    extra: z.string().nullish(),
    scope: z.enum(["org", "platform"]).optional(),
});
